// The UI language. The English text is the key: a string without a translation shows in English.
// Switching reloads the page, so components read `t()` at render time without a React context.

import moment from 'moment';
import 'moment/locale/ru';
import { nouns as ruNouns, strings as ruStrings } from 'i18n/ru';

export type Language = 'ru' | 'en';

const STORAGE_KEY = 'cvat-ui-language';
const ENGLISH_PLURALS: Record<string, string> = { category: 'categories', photo: 'photos', match: 'matches' };

function readLanguage(): Language {
    // the tests assert the English texts
    if (process.env.NODE_ENV === 'test') return 'en';
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored === 'ru' || stored === 'en') return stored;
    } catch {
        // storage blocked: fall through to the default
    }
    return 'ru';
}

export const language: Language = readLanguage();
if (typeof document !== 'undefined') document.documentElement.lang = language;

export function setLanguage(next: Language): void {
    try {
        localStorage.setItem(STORAGE_KEY, next);
    } catch {
        return;
    }
    window.location.reload();
}

type Params = Record<string, string | number>;

function fill(template: string, params?: Params): string {
    if (!params) return template;
    return template.replace(/\{(\w+)\}/g, (match, key: string) => (key in params ? String(params[key]) : match));
}

export function translate(lang: Language, text: string, params?: Params): string {
    return fill(lang === 'ru' ? ruStrings[text] ?? text : text, params);
}

// 1 / 2–4 / 5–20 and 11–14: the three Russian forms
export function russianForm(n: number): 0 | 1 | 2 {
    const tens = Math.abs(n) % 100;
    const units = tens % 10;
    if (tens > 10 && tens < 20) return 2;
    if (units === 1) return 0;
    if (units > 1 && units < 5) return 1;
    return 2;
}

export function pluralize(lang: Language, n: number, noun: string): string {
    if (lang === 'ru' && ruNouns[noun]) return `${n} ${ruNouns[noun][russianForm(n)]}`;
    // "object:of" is the Russian genitive ("материал 2 объектов"); English has one form
    const english = noun.split(':')[0];
    if (n === 1) return `${n} ${english}`;
    return `${n} ${ENGLISH_PLURALS[english] ?? `${english}s`}`;
}

// "0 of 5 drawings"; in Russian the noun leads in its plural genitive ("чертежей: 0 из 5"), so the number
// never has to agree with it
export function pluralizeOf(lang: Language, done: number, total: number, noun: string): string {
    if (lang === 'ru' && ruNouns[noun]) return `${ruNouns[noun][2]}: ${done} из ${total}`;
    return `${done} of ${pluralize(lang, total, noun)}`;
}

export function t(text: string, params?: Params): string {
    return translate(language, text, params);
}

// "5 drawings" in the UI language
export function plural(n: number, noun: string): string {
    return pluralize(language, n, noun);
}

export function pluralOf(done: number, total: number, noun: string): string {
    return pluralizeOf(language, done, total, noun);
}

// dates carry the language themselves: the filters' query builder resets moment's global locale to English
export function fromNow(date: string | Date): string {
    return moment(date).locale(language).fromNow();
}

// "Oct 6, 2026" / "6 окт. 2026 г."
export function formatDay(date: string | Date): string {
    return moment(date).locale(language).format('ll');
}
